import { useAutoAnimate } from '@formkit/auto-animate/react';
import {
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  ScissorsIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import AttributeComponent from 'components/common/attribute';
import FormInput from 'components/common/form-input';
import Tooltip from 'components/layout/tooltip';
import {
  ceilingHeightPresets,
  levelDetailsQuestionLabels,
  peakHeightPresets
} from 'data/constants';
import { LevelType, PartialWithRequired } from 'data/types';
import { useUnassignImageFromStructureItemMutation } from 'features/api/project-api';
import {
  setSuggestedSpaces,
  suggestSpacesForLevel,
  unassignImageFromStructureItem,
  updateStructureItem,
  updateStructureItemAttribute
} from 'features/structure';
import { useAssignLevelLayoutImage } from 'hooks/use-assign-level-layout-image';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDrop } from 'react-dnd';
import axios from 'utils/api';
import { getLayoutAttachments } from 'utils/attachments';
import { loadStructureAttachments } from 'utils/data';
import { isDuctedPathway } from 'utils/project';
import { getAssignedImageForLevel, isLevelConditioned } from 'utils/structure';
import { errorModal } from 'utils/ui';
import { validateLevel } from 'utils/validation';

type LevelProps = {
  level: LevelType;
  showAttributes?: boolean;
  imagePreviewMode?: boolean;
  embedded?: boolean;
  onImageDropForCrop?: (attachmentId: number, levelUniqueId: string) => void;
};

export default function LevelComponent({
  level,
  showAttributes = true,
  imagePreviewMode = false,
  embedded = false,
  onImageDropForCrop
}: LevelProps) {
  const dispatch = useAppDispatch();
  const {
    structure,
    structureAttachments,
    structureImageAssignments,
    structureUniqueId,
    structureId
  } = useAppSelector((state) => state.structure);
  const levelIndex = structure
    .toReversed()
    .findIndex((l) => l.uniqueId === level.uniqueId);
  const [animate] = useAutoAnimate({ duration: 100 });
  const [rotatingPreviewImageId, setRotatingPreviewImageId] = useState<
    number | null
  >(null);
  const assignLevelLayoutImage = useAssignLevelLayoutImage();
  const [unassignImageFromStructureItemMutation] =
    useUnassignImageFromStructureItemMutation();

  const layoutAttachments = useMemo(
    () => getLayoutAttachments(structureAttachments),
    [structureAttachments]
  );

  const assignedImage = useMemo(
    () =>
      getAssignedImageForLevel(
        level.uniqueId,
        structureImageAssignments,
        structureAttachments
      ),
    [level.uniqueId, structureImageAssignments, structureAttachments]
  );
  const heatingAttribute = level.attributes.find(
    (att) => att.code === 'heating'
  );
  const coolingAttribute = level.attributes.find(
    (att) => att.code === 'cooling'
  );
  const levelCeilingTypeAttribute = level.attributes.find(
    (att) => att.code === 'level_ceiling_type'
  );
  const ceilingHeightAttribute = level.attributes.find(
    (att) => att.code === 'ceiling_height'
  );
  const slopedHeightAttribute = level.attributes.find(
    (att) => att.code === 'sloped_height'
  );
  const vaultedHeightAttribute = level.attributes.find(
    (att) => att.code === 'vaulted_height'
  );
  const ductworkAttribute = level.attributes.find(
    (att) => att.code === 'level_ductwork'
  );
  const showDuctworkAttribute = isDuctedPathway() && !!ductworkAttribute;

  useEffect(() => {
    const validatedLevel = validateLevel(level);

    dispatch(updateStructureItem(validatedLevel));
  }, [
    JSON.stringify(
      level.sections.map((section) =>
        section.spaces.map((space) => ({
          id: space.uniqueId,
          attributes: space.attributes,
          BTUs: space.BTUs,
          conditioned: space.conditioned
        }))
      )
    )
  ]);

  const unassignExistingImage = useCallback(async () => {
    if (!assignedImage) return;
    dispatch(
      unassignImageFromStructureItem({ attachmentId: assignedImage.id })
    );
    try {
      await unassignImageFromStructureItemMutation({
        attachmentId: assignedImage.id
      }).unwrap();
    } catch (error) {
      console.error('Failed to unassign existing image:', error);
    }
  }, [assignedImage, dispatch, unassignImageFromStructureItemMutation]);

  const handleImageDrop = useCallback(
    async (attachmentId: number) => {
      await unassignExistingImage();

      const droppedAttachment = structureAttachments.find(
        (a) => a.id === attachmentId
      );
      const isAlreadyCropped =
        droppedAttachment?.name?.includes('(Cropped)') ?? false;

      if (onImageDropForCrop && !isAlreadyCropped) {
        onImageDropForCrop(attachmentId, level.uniqueId);
        return;
      }
      await assignLevelLayoutImage(attachmentId, level.uniqueId);
      dispatch(suggestSpacesForLevel({ levelUniqueId: level.uniqueId }));
    },
    [
      level.uniqueId,
      dispatch,
      assignLevelLayoutImage,
      onImageDropForCrop,
      unassignExistingImage,
      structureAttachments
    ]
  );

  const [{ isOver }, dropRef] = useDrop(
    () => ({
      accept: ['structure-image'],
      drop: (item: { id: number }) => handleImageDrop(item.id),
      collect: (monitor) => ({ isOver: !!monitor.isOver() })
    }),
    [handleImageDrop]
  );

  const handleUnassignImage = useCallback(async () => {
    if (!assignedImage) return;

    dispatch(
      unassignImageFromStructureItem({ attachmentId: assignedImage.id })
    );
    dispatch(
      setSuggestedSpaces({
        levelUniqueId: level.uniqueId,
        suggestedSpaces: []
      })
    );
    try {
      await unassignImageFromStructureItemMutation({
        attachmentId: assignedImage.id
      }).unwrap();
    } catch (error) {
      console.error('Failed to unassign image from level:', error);
    }
  }, [
    assignedImage,
    dispatch,
    unassignImageFromStructureItemMutation,
    level.uniqueId
  ]);

  const handleRotatePreviewImage = useCallback(
    async (direction: 'left' | 'right') => {
      if (!assignedImage || !structureUniqueId || !structureId) return;

      setRotatingPreviewImageId(assignedImage.id);
      try {
        await axios.post('structure/rotate-structure-image', {
          attachmentId: assignedImage.id,
          structureUniqueId,
          direction
        });
        await loadStructureAttachments(structureId);
      } catch (error) {
        console.error('Error rotating image:', error);
        errorModal('Failed to rotate image');
      } finally {
        setRotatingPreviewImageId(null);
      }
    },
    [assignedImage, structureUniqueId, structureId]
  );

  const handleCropPreviewImage = useCallback(() => {
    if (!assignedImage || !onImageDropForCrop) return;
    onImageDropForCrop(assignedImage.id, level.uniqueId);
  }, [assignedImage, onImageDropForCrop, level.uniqueId]);

  const shouldGetCeilingHeight = useMemo(() => {
    return isLevelConditioned(level);
  }, [level]);

  const handleChangeConditioned = (value: boolean | undefined) => {
    dispatch(
      updateStructureItem({
        uniqueId: level.uniqueId,
        conditioned: value
      } as PartialWithRequired<LevelType, 'uniqueId'>)
    );
  };

  const handleChangeAttribute = (attributeId: number, value: any) => {
    dispatch(
      updateStructureItemAttribute({
        uniqueId: level.uniqueId,
        attributeId,
        value
      })
    );
  };

  const classes = clsx(
    'w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-colors'
  );

  const headerClasses = clsx(
    'flex h-10 w-full items-center border-b border-gray-200',
    'bg-white'
  );

  const containerWrapperClasses = 'relative flex';

  const renderImagePreview = () => {
    if (assignedImage) {
      const isRotating = rotatingPreviewImageId === assignedImage.id;
      return (
        <div className='flex w-full grow flex-col items-center gap-1'>
          <div className='flex items-center gap-2 pb-2'>
            <button
              className='relative cursor-pointer text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50'
              type='button'
              disabled={isRotating}
              onClick={() => handleRotatePreviewImage('left')}
            >
              <ArrowUturnLeftIcon className='h-5 w-5' />
              <Tooltip text='Rotate Left' />
            </button>
            <button
              className='relative cursor-pointer text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50'
              type='button'
              disabled={isRotating}
              onClick={() => handleRotatePreviewImage('right')}
            >
              <ArrowUturnRightIcon className='h-5 w-5' />
              <Tooltip text='Rotate Right' />
            </button>
            {!assignedImage.name?.includes('(Cropped)') && (
              <button
                className='relative cursor-pointer text-gray-500 hover:text-gray-700'
                type='button'
                onClick={handleCropPreviewImage}
              >
                <ScissorsIcon className='h-5 w-5' />
                <Tooltip text='Crop Image' />
              </button>
            )}
            <button
              className='relative cursor-pointer text-gray-500 hover:text-gray-700'
              type='button'
              onClick={handleUnassignImage}
            >
              <XMarkIcon className='h-5 w-5' />
              <Tooltip text='Unassign Image' />
            </button>
          </div>
          <img
            src={assignedImage.url ?? ''}
            alt={assignedImage.name}
            className='h-[120px] w-full rounded object-contain'
            draggable={false}
          />
          <span className='w-full truncate text-center text-xs'>
            {assignedImage.name}
          </span>
        </div>
      );
    }

    return (
      <div className='flex w-full flex-col items-center justify-center gap-2 py-6 text-center'>
        <span className='max-w-xs text-sm text-gray-500'>
          {layoutAttachments.length === 0
            ? 'Upload an image on the left first.'
            : 'Drag an image here to assign it to this floor.'}
        </span>
      </div>
    );
  };

  return (
    <div className='w-full'>
      <div className={embedded ? 'w-full' : classes}>
        {!embedded && (
          <div className={headerClasses}>
            <span className='grow pl-3 text-left font-medium text-gray-800'>
              {level.label}
            </span>
          </div>
        )}
        <div
          className={containerWrapperClasses}
          id={`level-${levelIndex}-details`}
        >
          {imagePreviewMode && (
            <div
              ref={dropRef}
              className={clsx(
                'flex w-full grow rounded-lg border-2 border-dashed p-3 transition-colors',
                isOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
              )}
            >
              {renderImagePreview()}
            </div>
          )}
          {!imagePreviewMode && (
            <div
              ref={animate}
              className='flex w-full flex-col items-center gap-5 px-3 py-6'
            >
              {showAttributes && (
                <>
                  {level.type !== 'crawlspace' && (
                    <>
                      <FormInput
                        type='select'
                        inlineLabel
                        label={levelDetailsQuestionLabels.conditioned}
                        tooltip='Yes, if the level is currently heated or cooled. This is used to improve the accuracy of the estimated heating and cooling load requirements of your structure.'
                        tooltipLocation='after_label'
                        value={
                          // eslint-disable-next-line no-nested-ternary
                          level.conditioned !== undefined
                            ? level.conditioned
                              ? 'Yes'
                              : 'No'
                            : undefined
                        }
                        onChange={(value) =>
                          handleChangeConditioned(
                            value === 'No' ? false : value === 'Yes'
                          )
                        }
                        options={['Yes', 'No']}
                        required
                        completed={level.conditioned !== undefined}
                        showProgress
                      />
                      {heatingAttribute && (
                        <AttributeComponent
                          {...heatingAttribute}
                          inlineLabel
                          label={levelDetailsQuestionLabels.heating}
                          tooltip='Full, if you plan to add new primary heating to the level, Backup if you plan to add new supplemental heating to the level, or None if you do not plan to add any heating to the level.'
                          tooltipLocation='after_label'
                          options={[
                            { value: 'Primary', label: 'Full' },
                            { value: 'Supplemental', label: 'Backup' },
                            { value: 'None', label: 'None' }
                          ]}
                          onChange={(value) =>
                            handleChangeAttribute(heatingAttribute.id, value)
                          }
                          completed={!!heatingAttribute.value}
                          required
                        />
                      )}
                      {coolingAttribute && (
                        <AttributeComponent
                          {...coolingAttribute}
                          inlineLabel
                          label={levelDetailsQuestionLabels.cooling}
                          tooltip='Yes, if you plan to add new primary cooling to the level, or No if you do not plan to add any cooling to the level.'
                          tooltipLocation='after_label'
                          options={[
                            { value: 'Primary', label: 'Yes' },
                            { value: 'None', label: 'No' }
                          ]}
                          onChange={(value) =>
                            handleChangeAttribute(coolingAttribute.id, value)
                          }
                          completed={!!coolingAttribute.value}
                          required
                        />
                      )}
                      {showDuctworkAttribute && ductworkAttribute && (
                        <AttributeComponent
                          {...ductworkAttribute}
                          inlineLabel
                          label={levelDetailsQuestionLabels.level_ductwork}
                          tooltip='Full, if the level is fully served by existing ductwork, Partial if only some areas have ductwork, or None if the level has no existing ductwork.'
                          tooltipLocation='after_label'
                          options={['Full', 'Partial', 'None']}
                          onChange={(value) =>
                            handleChangeAttribute(ductworkAttribute.id, value)
                          }
                          completed={!!ductworkAttribute.value}
                          required
                        />
                      )}
                      {shouldGetCeilingHeight && ceilingHeightAttribute && (
                        <AttributeComponent
                          {...ceilingHeightAttribute}
                          tooltip='The average height of the ceiling on the level. You will also have the options to set ceiling height for each space, if needed. This is used to improve the accuracy of the estimated heating and cooling load requirements of your structure.'
                          tooltipLocation='after_label'
                          onChange={(value) =>
                            handleChangeAttribute(
                              ceilingHeightAttribute.id,
                              value
                            )
                          }
                          completed={!!ceilingHeightAttribute.value}
                          inlineLabel
                          label={levelDetailsQuestionLabels.ceiling_height}
                          presets={ceilingHeightPresets}
                          required
                        />
                      )}
                      {level.type === 'level' &&
                        levelCeilingTypeAttribute &&
                        isLevelConditioned(level) && (
                          <AttributeComponent
                            {...levelCeilingTypeAttribute}
                            inlineLabel
                            label={
                              levelDetailsQuestionLabels.level_ceiling_type
                            }
                            tooltip='The primary type of ceiling on the level. This is used to help determine which air handling unit is appropriate for this level.'
                            tooltipLocation='after_label'
                            onChange={(value) =>
                              handleChangeAttribute(
                                levelCeilingTypeAttribute.id,
                                value
                              )
                            }
                            completed={!!levelCeilingTypeAttribute.value}
                            required
                          />
                        )}

                      {isLevelConditioned(level) &&
                        slopedHeightAttribute &&
                        levelCeilingTypeAttribute?.value === 'Sloped' && (
                          <AttributeComponent
                            tooltip='The peak height of the sloped ceiling on the level.'
                            tooltipLocation='after_label'
                            {...slopedHeightAttribute}
                            inlineLabel
                            label={levelDetailsQuestionLabels.sloped_height}
                            presets={peakHeightPresets}
                            onChange={(value) =>
                              handleChangeAttribute(
                                slopedHeightAttribute.id,
                                value
                              )
                            }
                            completed={!!slopedHeightAttribute.value}
                            required
                          />
                        )}
                      {isLevelConditioned(level) &&
                        vaultedHeightAttribute &&
                        levelCeilingTypeAttribute?.value === 'Vaulted' && (
                          <AttributeComponent
                            tooltip='The peak height of the vaulted ceiling on the level.'
                            tooltipLocation='after_label'
                            {...vaultedHeightAttribute}
                            inlineLabel
                            label={levelDetailsQuestionLabels.vaulted_height}
                            presets={peakHeightPresets}
                            onChange={(value) =>
                              handleChangeAttribute(
                                vaultedHeightAttribute.id,
                                value
                              )
                            }
                            completed={!!vaultedHeightAttribute.value}
                            required
                          />
                        )}
                    </>
                  )}
                  {level.type === 'crawlspace' && (
                    <>
                      {ceilingHeightAttribute && (
                        <AttributeComponent
                          {...ceilingHeightAttribute}
                          onChange={(value) =>
                            handleChangeAttribute(
                              ceilingHeightAttribute.id,
                              value
                            )
                          }
                          completed={!!ceilingHeightAttribute.value}
                          inlineLabel
                          label={levelDetailsQuestionLabels.crawlspace_height}
                          tooltip='The height of the crawlspace from the ground to the ceiling.'
                          tooltipLocation='after_label'
                          required
                        />
                      )}
                      {showDuctworkAttribute && ductworkAttribute && (
                        <AttributeComponent
                          {...ductworkAttribute}
                          inlineLabel
                          label={levelDetailsQuestionLabels.crawlspace_ductwork}
                          tooltip='Full, if ductwork runs through the crawlspace, Partial if only some areas have ductwork, or None if the crawlspace has no existing ductwork.'
                          tooltipLocation='after_label'
                          options={['Full', 'Partial', 'None']}
                          onChange={(value) =>
                            handleChangeAttribute(ductworkAttribute.id, value)
                          }
                          completed={!!ductworkAttribute.value}
                          required
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
